import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { 
  UserIcon, 
  PhoneIcon, 
  EnvelopeIcon,
  MapPinIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  StarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  CalendarIcon,
  DocumentTextIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  HeartIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'

const BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL || 'https://sayhi-voice-engine-production.up.railway.app'

export default function Customers() {
  const { getUserBusinessPhones, isSuperAdmin, userBusinesses, user } = useAuth()
  const [customers, setCustomers] = useState([])
  const [businesses, setBusinesses] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('recent')
  const [filterType, setFilterType] = useState('all')
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [showCustomerModal, setShowCustomerModal] = useState(false)
  const [customerCalls, setCustomerCalls] = useState([])
  const [loadingCalls, setLoadingCalls] = useState(false)
  const [error, setError] = useState(null)
  const [stats, setStats] = useState({
    totalCustomers: 0,
    newThisWeek: 0,
    returningCustomers: 0,
    vipCustomers: 0
  })

  useEffect(() => {
    loadCustomersAndBusinesses()
  }, [sortBy, filterType])

  const getAuthToken = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    return session?.access_token
  }

  const loadCustomersAndBusinesses = async () => {
    setLoading(true)
    setError(null)
    try {
      const authToken = await getAuthToken()
      
      if (!authToken) {
        throw new Error('No authentication token available')
      }

      // Use backend API for secure data fetching
      const params = new URLSearchParams({
        sortBy,
        filterType
      })

      const [customersResponse, businessesResponse] = await Promise.all([
        fetch(`${BACKEND_URL}/api/dashboard/customers?${params}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          }
        }),
        fetch(`${BACKEND_URL}/api/dashboard/businesses`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          }
        })
      ])

      if (!customersResponse.ok) {
        const errorData = await customersResponse.json().catch(() => ({}))
        throw new Error(errorData.error || `Customers API error: ${customersResponse.status}`)
      }

      if (!businessesResponse.ok) {
        const errorData = await businessesResponse.json().catch(() => ({}))
        throw new Error(errorData.error || `Businesses API error: ${businessesResponse.status}`)
      }

      const customersData = await customersResponse.json()
      const businessesData = await businessesResponse.json()

      if (!customersData.success) {
        throw new Error(customersData.error || 'Failed to fetch customers')
      }

      if (!businessesData.success) {
        throw new Error(businessesData.error || 'Failed to fetch businesses')
      }

      // Create a map of businesses by ID for quick lookup
      const businessMap = new Map()
      businessesData.businesses.forEach(business => {
        businessMap.set(business.id, business)
      })

      // Merge customers with business data
      const customersWithBusinesses = customersData.customers.map(customer => ({
        ...customer,
        business_info: businessMap.get(customer.business_id) || null
      }))

      setCustomers(customersWithBusinesses)
      setBusinesses(businessesData.businesses)
      calculateStats(customersWithBusinesses)

    } catch (error) {
      console.error('Error loading customers and businesses:', error)
      setError(error.message)
      setCustomers([])
      setBusinesses([])
      calculateStats([])
    } finally {
      setLoading(false)
    }
  }

  const calculateStats = (customersData) => {
    const total = customersData.length
    
    const weekAgo = new Date()
    weekAgo.setDate(weekAgo.getDate() - 7)
    
    const newThisWeek = customersData.filter(customer => 
      customer.first_call_date && new Date(customer.first_call_date) >= weekAgo
    ).length
    
    const returning = customersData.filter(customer => 
      customer.total_calls > 1
    ).length
    
    const vip = customersData.filter(customer => 
      customer.vip_status
    ).length

    setStats({
      totalCustomers: total,
      newThisWeek,
      returningCustomers: returning,
      vipCustomers: vip
    })
  }

  const filteredCustomers = customers.filter(customer => {
    if (!searchTerm) return true
    const searchLower = searchTerm.toLowerCase()
    return (
      customer.name?.toLowerCase().includes(searchLower) ||
      customer.phone_number?.toLowerCase().includes(searchLower) ||
      customer.email?.toLowerCase().includes(searchLower) ||
      customer.address?.toLowerCase().includes(searchLower) ||
      customer.business_info?.business_name?.toLowerCase().includes(searchLower)
    )
  })

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'No phone number'
    const cleaned = phone.replace(/\D/g, '')
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      const number = cleaned.slice(1)
      return `(${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`
    }
    return phone
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getCustomerTypeLabel = (customer) => {
    if (customer.vip_status) return 'VIP Customer'
    if (customer.total_calls > 5) return 'Frequent Caller'
    if (customer.total_calls > 1) return 'Returning Customer'
    return 'New Customer'
  }

  const getCustomerTypeColor = (customer) => {
    if (customer.vip_status) return 'bg-purple-100 text-purple-800 border-purple-200'
    if (customer.total_calls > 5) return 'bg-blue-100 text-blue-800 border-blue-200'
    if (customer.total_calls > 1) return 'bg-green-100 text-green-800 border-green-200'
    return 'bg-gray-100 text-gray-800 border-gray-200'
  }

  const openCustomerDetails = async (customer) => {
    setSelectedCustomer(customer)
    setShowCustomerModal(true)
    setLoadingCalls(true)

    try {
      const authToken = await getAuthToken()
      
      if (!authToken) {
        throw new Error('No authentication token available')
      }

      // Load customer's call history from backend
      const response = await fetch(`${BACKEND_URL}/api/dashboard/customers/${customer.id}/calls`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to load customer calls')
      }

      const data = await response.json()
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to load customer calls')
      }

      setCustomerCalls(data.calls || [])
    } catch (error) {
      console.error('Error loading customer calls:', error)
      setCustomerCalls([])
    } finally {
      setLoadingCalls(false)
    }
  }

  const toggleVipStatus = async (customer) => {
    try {
      const authToken = await getAuthToken()
      
      if (!authToken) {
        throw new Error('No authentication token available')
      }

      const response = await fetch(`${BACKEND_URL}/api/customers/${customer.id}/vip`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vip_status: !customer.vip_status
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to update VIP status')
      }

      const data = await response.json()
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to update VIP status')
      }

      // Update local state
      setCustomers(customers.map(c => 
        c.id === customer.id 
          ? { ...c, vip_status: !c.vip_status }
          : c
      ))

      if (selectedCustomer && selectedCustomer.id === customer.id) {
        setSelectedCustomer({ ...selectedCustomer, vip_status: !selectedCustomer.vip_status })
      }

      // Recalculate stats
      const updatedCustomers = customers.map(c => 
        c.id === customer.id 
          ? { ...c, vip_status: !c.vip_status }
          : c
      )
      calculateStats(updatedCustomers)

    } catch (error) {
      console.error('Error updating VIP status:', error)
      // Could add a toast notification here
    }
  }

  const getCallTypeLabel = (call) => {
    if (call.emergency_escalation || call.call_type === 'emergency') {
      return 'Emergency'
    }
    if (call.call_type === 'escalation') {
      return 'Escalated'
    }
    return 'Regular'
  }

  const getCallTypeColor = (call) => {
    if (call.emergency_escalation || call.call_type === 'emergency') {
      return 'bg-red-100 text-red-800'
    }
    if (call.call_type === 'escalation') {
      return 'bg-orange-100 text-orange-800'
    }
    return 'bg-green-100 text-green-800'
  }

  const formatCallTime = (dateString) => {
    if (!dateString) return 'Unknown time'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  const getCallDuration = (call) => {
    const seconds = call.call_duration_seconds || call.duration_seconds || 0
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your customers...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Customers</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadCustomersAndBusinesses}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-lg border-b">
        <div className="px-6 py-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <UserGroupIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Customer Directory</h1>
                <p className="text-sm text-gray-600">Manage and track all your customer relationships</p>
              </div>
            </div>
            <button
              onClick={loadCustomersAndBusinesses}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-sm transition-all duration-200"
            >
              Refresh
            </button>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="lg:col-span-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search customers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
                />
              </div>
            </div>

            {/* Sort By */}
            <div>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
              >
                <option value="recent">Most Recent</option>
                <option value="name">Name A-Z</option>
                <option value="calls">Most Calls</option>
                <option value="value">Highest Value</option>
              </select>
            </div>

            {/* Filter Type */}
            <div>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
              >
                <option value="all">All Customers</option>
                <option value="vip">VIP Only</option>
                <option value="returning">Returning Customers</option>
                <option value="new">New This Week</option>
              </select>
            </div>

            {/* Stats Summary */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg px-3 py-2 border border-blue-200">
              <div className="text-center">
                <p className="text-sm font-medium text-blue-900">{stats.totalCustomers} Total</p>
                <p className="text-xs text-blue-600">{stats.newThisWeek} new this week</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Total Customers</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalCustomers}</p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <UserGroupIcon className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">New This Week</p>
                <p className="text-2xl font-bold text-gray-900">{stats.newThisWeek}</p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                <ArrowTrendingUpIcon className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Returning</p>
                <p className="text-2xl font-bold text-gray-900">{stats.returningCustomers}</p>
              </div>
              <div className="h-12 w-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <CheckCircleIcon className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">VIP Customers</p>
                <p className="text-2xl font-bold text-gray-900">{stats.vipCustomers}</p>
              </div>
              <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <StarIconSolid className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Customers List */}
        <div className="bg-white shadow-sm rounded-xl border border-gray-200 overflow-hidden">
          {filteredCustomers.length === 0 ? (
            <div className="text-center py-16">
              <UserGroupIcon className="mx-auto h-16 w-16 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No customers found</h3>
              <p className="text-gray-500 mb-4">
                {customers.length === 0 
                  ? "No customers have called yet. When customers call, they'll appear here."
                  : "Try adjusting your filters to see more customers."
                }
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredCustomers.map((customer, index) => (
                <div 
                  key={customer.id || index} 
                  className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => openCustomerDetails(customer)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="h-12 w-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                        {customer.vip_status ? (
                          <StarIconSolid className="h-6 w-6 text-yellow-300" />
                        ) : (
                          <UserIcon className="h-6 w-6 text-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="text-sm font-medium text-gray-900">
                            {customer.name || 'Unknown Customer'}
                          </h3>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getCustomerTypeColor(customer)}`}>
                            {getCustomerTypeLabel(customer)}
                          </span>
                          {customer.vip_status && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                toggleVipStatus(customer)
                              }}
                              className="text-purple-600 hover:text-purple-800"
                            >
                              <StarIconSolid className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <div className="flex items-center">
                            <PhoneIcon className="h-4 w-4 mr-1" />
                            {formatPhoneNumber(customer.phone_number)}
                          </div>
                          {customer.email && (
                            <div className="flex items-center">
                              <EnvelopeIcon className="h-4 w-4 mr-1" />
                              {customer.email}
                            </div>
                          )}
                          <div className="flex items-center">
                            <PhoneIcon className="h-4 w-4 mr-1" />
                            {customer.total_calls} calls
                          </div>
                        </div>
                        <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                          {customer.address && (
                            <div className="flex items-center">
                              <MapPinIcon className="h-3 w-3 mr-1" />
                              {customer.address}
                            </div>
                          )}
                          {customer.business_info && (
                            <div className="flex items-center">
                              <BuildingOfficeIcon className="h-3 w-3 mr-1" />
                              {customer.business_info.business_name}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Last call: {formatDate(customer.last_call_date)}</p>
                      <p className="text-xs text-gray-500 mt-1">Customer since: {formatDate(customer.first_call_date)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Customer Details Modal - Keep your existing modal JSX here */}
      {showCustomerModal && selectedCustomer && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <div className="h-16 w-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                  {selectedCustomer.vip_status ? (
                    <StarIconSolid className="h-8 w-8 text-yellow-300" />
                  ) : (
                    <UserIcon className="h-8 w-8 text-white" />
                  )}
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {selectedCustomer.name || 'Unknown Customer'}
                  </h3>
                  <div className="flex items-center space-x-2">
                    <p className="text-gray-600">{getCustomerTypeLabel(selectedCustomer)}</p>
                    {selectedCustomer.business_info && (
                      <>
                        <span className="text-gray-400">•</span>
                        <p className="text-gray-600">{selectedCustomer.business_info.business_name}</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowCustomerModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Customer Information */}
              <div className="lg:col-span-1 space-y-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <UserIcon className="h-5 w-5 mr-2" />
                    Contact Information
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">Phone Number</label>
                      <p className="text-gray-900">{formatPhoneNumber(selectedCustomer.phone_number)}</p>
                    </div>
                    {selectedCustomer.email && (
                      <div>
                        <label className="text-sm font-medium text-gray-600">Email</label>
                        <p className="text-gray-900">{selectedCustomer.email}</p>
                      </div>
                    )}
                    {selectedCustomer.address && (
                      <div>
                        <label className="text-sm font-medium text-gray-600">Address</label>
                        <p className="text-gray-900">{selectedCustomer.address}</p>
                      </div>
                    )}
                    <div>
                      <label className="text-sm font-medium text-gray-600">Preferred Contact</label>
                      <p className="text-gray-900 capitalize">{selectedCustomer.contact_preference || 'Phone'}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-6">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <ClockIcon className="h-5 w-5 mr-2" />
                    Customer Stats
                  </h4>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-blue-600">{selectedCustomer.total_calls}</p>
                    <p className="text-sm text-gray-600">Total Calls</p>
                  </div>
                  <div className="mt-4 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">First Call:</span>
                      <span className="text-sm font-medium">{formatDate(selectedCustomer.first_call_date)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Last Call:</span>
                      <span className="text-sm font-medium">{formatDate(selectedCustomer.last_call_date)}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between bg-white rounded-lg border p-4">
                  <div className="flex items-center">
                    <StarIcon className="h-5 w-5 text-gray-400 mr-2" />
                    <span className="text-sm font-medium text-gray-700">VIP Status</span>
                  </div>
                  <button
                    onClick={() => toggleVipStatus(selectedCustomer)}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      selectedCustomer.vip_status 
                        ? 'bg-purple-100 text-purple-800 hover:bg-purple-200' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {selectedCustomer.vip_status ? (
                      <>
                        <StarIconSolid className="h-4 w-4 mr-1" />
                        VIP Customer
                      </>
                    ) : (
                      <>
                        <StarIcon className="h-4 w-4 mr-1" />
                        Make VIP
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Call History */}
              <div className="lg:col-span-2">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <PhoneIcon className="h-5 w-5 mr-2" />
                    Call History
                  </h4>
                  
                  {loadingCalls ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                      <p className="text-gray-600">Loading call history...</p>
                    </div>
                  ) : customerCalls.length === 0 ? (
                    <div className="text-center py-8">
                      <PhoneIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <p className="text-gray-600">No call history found</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {customerCalls.map((call, index) => (
                        <div key={call.call_id || index} className="bg-white rounded-lg p-4 border border-gray-200">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCallTypeColor(call)}`}>
                                {getCallTypeLabel(call)}
                              </span>
                              <span className="text-sm text-gray-600">{formatCallTime(call.call_start_time)}</span>
                            </div>
                            <span className="text-sm text-gray-500">{getCallDuration(call)}</span>
                          </div>
                          {call.conversation_summary && (
                            <p className="text-sm text-gray-700 mb-2">
                              {call.conversation_summary.length > 150 
                                ? call.conversation_summary.substring(0, 150) + '...'
                                : call.conversation_summary}
                            </p>
                          )}
                          {call.services_mentioned && call.services_mentioned.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {call.services_mentioned.slice(0, 3).map((service, i) => (
                                <span key={i} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  {service}
                                </span>
                              ))}
                              {call.services_mentioned.length > 3 && (
                                <span className="text-xs text-gray-500">+{call.services_mentioned.length - 3} more</span>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowCustomerModal(false)}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}